import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { GET_PORTALIO_PROPOSALS_QUERY } from 'src/graphql/entities/integrations/portalio/portalio.operations';

export type PortalioProposalItem = {
  id: number;
  status: string;
  name: string;
  proposalNumber: number;
  createdDate: string;
  lastModifiedDate: string;
  customer?: {
    id: number;
    firstName: string;
    lastName?: string;
  };
};

export enum PortalioProposalStatuses {
  UNDEFINED = 'Undefined',
  DRAFT = 'Draft',
  SUBMITTED = 'Submitted',
  VIEWED_BY_CLIENT = 'ViewedByClient',
  ACCEPTED = 'Accepted',
  DECLINED = 'Declined',
  DELAYED = 'Delayed',
  COMPLETED = 'Completed',
  EMAIL_FAILED = 'EmailFailed',
  EXPIRED = 'Expired',
}

type PortalioProposalStatusValues =
  (typeof PortalioProposalStatuses)[keyof typeof PortalioProposalStatuses];

export type GetPortalioProposalsInput = {
  searchValue?: string;
  statuses?: PortalioProposalStatusValues[];
  isArchive?: boolean;
  pageNumber?: number;
  pageSize?: number;
};

export type GetPortalioProposalsParams = {
  params: GetPortalioProposalsInput;
};

export type GetPortalioProposalsQuery = {
  getPortalioProposals: {
    records?: PortalioProposalItem[];
    total: number;
  };
};

export const useGetPortalioProposals = ({
  searchValue,
  isArchive = false,
  pageSize = 10,
  pageNumber = 1,
}: GetPortalioProposalsInput) => {
  const params = useMemo<GetPortalioProposalsInput>(() => {
    return { searchValue, isArchive, pageSize, pageNumber };
  }, [searchValue, isArchive, pageSize, pageNumber]);
  const { loading, data, fetchMore } = useQuery<GetPortalioProposalsQuery, GetPortalioProposalsParams>(
    GET_PORTALIO_PROPOSALS_QUERY,
    {
      variables: {
        params,
      },
    },
  );

  return {
    loading,
    proposals: data?.getPortalioProposals.records,
    total: data?.getPortalioProposals.total,
    fetchMore,
  };
};
